import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useStepScormUploader } from '@/features/StepScormUploads';
import { useStepVideoUploader } from '@/features/StepVideoUploads';
import {
  type UpdateContentStepRequest,
  useCreateLessonStepMutation,
  useDeleteContentStepMutation,
  useDeleteContentStepScormMutation,
  useDeleteContentStepVideoMutation,
  useReorderLessonStepsMutation,
  useUpdateContentStepMutation,
  useUpdateContentStepScormMutation,
} from '@/entities/Courses';
import { useToast } from '@/shared/lib/hooks/useToast';
import { getSaveErrorMessage } from './useSaveSessionSteps';
import {
  createSessionStep,
  emptyContentStep,
  type SessionStepDraft,
  type StepLiveActions,
} from './types';

const ADD_KEY = '__add__';

const isNotFound = (error: unknown) => (error as { status?: number })?.status === 404;

const getFieldChanges = (step: SessionStepDraft): UpdateContentStepRequest => {
  const existing = step.existing;
  const body: UpdateContentStepRequest = {};
  if (!existing) return body;
  if (step.type && step.type !== existing.type) body.type = step.type;
  if (step.title.trim() !== (existing.title ?? '')) body.title = step.title.trim();
  if (step.description !== (existing.description ?? '')) body.description = step.description;
  if (step.content !== (existing.content ?? '')) body.content = step.content;
  return body;
};

interface UseLiveSessionStepsParams {
  lessonId?: number;
  // First line of the step's video upload in the uploads tray
  lessonTitle?: string;
  setSteps: Dispatch<SetStateAction<SessionStepDraft[]>>;
}

/**
 * Edit mode of the steps editor: each action is saved immediately. Video and SCORM uploads go to
 * the uploads managers and are not part of `isBusy`: they keep running after the modal closes.
 */
export const useLiveSessionSteps = ({
  lessonId,
  lessonTitle,
  setSteps,
}: UseLiveSessionStepsParams) => {
  const { toast } = useToast();
  const videoUploader = useStepVideoUploader();
  const scormUploader = useStepScormUploader();
  const [createLessonStep] = useCreateLessonStepMutation();
  const [updateContentStep] = useUpdateContentStepMutation();
  const [deleteContentStep] = useDeleteContentStepMutation();
  const [reorderLessonSteps] = useReorderLessonStepsMutation();
  const [updateContentStepScorm] = useUpdateContentStepScormMutation();
  const [deleteContentStepVideo] = useDeleteContentStepVideoMutation();
  const [deleteContentStepScorm] = useDeleteContentStepScormMutation();
  // Number of running requests per step (clientId)
  const [saving, setSaving] = useState<Record<string, number>>({});

  const patchStep = useCallback(
    (clientId: string, patch: (step: SessionStepDraft) => Partial<SessionStepDraft>) => {
      setSteps((current) =>
        current.map((item) => (item.clientId === clientId ? { ...item, ...patch(item) } : item)),
      );
    },
    [setSteps],
  );

  const run = useCallback(
    async (key: string, task: () => Promise<void>, fallback: string) => {
      setSaving((current) => ({ ...current, [key]: (current[key] ?? 0) + 1 }));
      try {
        await task();
        return true;
      } catch (error) {
        toast({ title: 'Error', description: getSaveErrorMessage(error, fallback), color: 'danger' });
        return false;
      } finally {
        setSaving((current) => {
          const next = { ...current, [key]: (current[key] ?? 1) - 1 };
          if (next[key] <= 0) delete next[key];
          return next;
        });
      }
    },
    [toast],
  );

  const actions = useMemo<StepLiveActions>(
    () => ({
      addStep: async () => {
        if (!lessonId) return;
        await run(
          ADD_KEY,
          async () => {
            const created = await createLessonStep({ lessonId, body: {} }).unwrap();
            const stepId = created.data.step.id;
            setSteps((current) => [
              ...current.map((item) => ({ ...item, expanded: false })),
              {
                ...createSessionStep(),
                serverId: stepId,
                existing: emptyContentStep(stepId, current.length + 1),
              },
            ]);
          },
          'Failed to add a step',
        );
      },

      saveFields: async (step, overrides) => {
        const merged = { ...step, ...overrides };
        const stepId = merged.serverId;
        const changes = getFieldChanges(merged);
        if (!stepId || Object.keys(changes).length === 0) return;
        await run(
          step.clientId,
          async () => {
            await updateContentStep({ stepId, body: changes }).unwrap();
            patchStep(step.clientId, (item) => ({
              existing: item.existing && {
                ...item.existing,
                type: changes.type ?? item.existing.type,
                title: changes.title ?? item.existing.title,
                description: changes.description ?? item.existing.description,
                content: changes.content ?? item.existing.content,
              },
            }));
          },
          'Failed to save the step',
        );
      },

      uploadAttachments: async (step, files) => {
        const stepId = step.serverId;
        if (!stepId || files.length === 0) return;
        await run(
          step.clientId,
          async () => {
            await updateContentStep({ stepId, body: { attachments: files } }).unwrap();
          },
          'Failed to upload the files',
        );
        // Uploaded files come back as saved files of the step; failed ones are dropped
        patchStep(step.clientId, (item) => ({
          attachments: item.attachments.filter((file) => !files.includes(file)),
        }));
      },

      removeAttachment: async (step, key) => {
        const stepId = step.serverId;
        if (!stepId) return;
        patchStep(step.clientId, (item) => ({
          removedAttachmentKeys: [...item.removedAttachmentKeys, key],
        }));
        const ok = await run(
          step.clientId,
          async () => {
            await updateContentStep({ stepId, body: { removeAttachmentKeys: [key] } }).unwrap();
          },
          'Failed to delete the file',
        );
        if (!ok) {
          patchStep(step.clientId, (item) => ({
            removedAttachmentKeys: item.removedAttachmentKeys.filter((removed) => removed !== key),
          }));
        }
      },

      deleteStep: async (step) => {
        const stepId = step.serverId;
        const ok = await run(
          step.clientId,
          async () => {
            if (!stepId) return;
            try {
              await deleteContentStep(stepId).unwrap();
            } catch (error) {
              if (!isNotFound(error)) throw error;
            }
          },
          'Failed to delete the step',
        );
        if (ok) {
          // The step is gone together with its video or package: a failed upload has nothing to retry
          if (stepId) {
            videoUploader.forget(stepId);
            scormUploader.forget(stepId);
          }
          setSteps((current) => current.filter((item) => item.clientId !== step.clientId));
        }
        return ok;
      },

      reorder: async (next, previous) => {
        if (!lessonId) return;
        const stepIds = next.map((item) => item.serverId).filter((id): id is number => !!id);
        const ok = await run(
          ADD_KEY,
          async () => {
            await reorderLessonSteps({ lessonId, body: { stepIds } }).unwrap();
          },
          'Failed to save the order of steps',
        );
        if (!ok) setSteps(previous);
      },

      saveScormSettings: async (step, graded, score) => {
        const stepId = step.serverId;
        if (!stepId) return;
        const ok = await run(
          step.clientId,
          async () => {
            await updateContentStepScorm({
              stepId,
              body: { scormHasAssessment: graded, ...(graded ? { scormPassingScore: score } : {}) },
            }).unwrap();
          },
          'Failed to save the SCORM settings',
        );
        if (!ok) {
          // Back to the values the step had before the change
          patchStep(step.clientId, () => ({
            scormGraded: step.scormGraded,
            scormPassingScore: step.scormPassingScore,
          }));
        }
      },

      removeVideo: async (step) => {
        const stepId = step.serverId;
        if (!stepId) return;
        const ok = await run(
          step.clientId,
          async () => {
            try {
              await deleteContentStepVideo(stepId).unwrap();
            } catch (error) {
              if (!isNotFound(error)) throw error;
            }
          },
          'Failed to delete the video',
        );
        if (ok) patchStep(step.clientId, () => ({ removeVideo: true }));
      },

      removeScorm: async (step) => {
        const stepId = step.serverId;
        if (!stepId) return;
        const ok = await run(
          step.clientId,
          async () => {
            try {
              await deleteContentStepScorm(stepId).unwrap();
            } catch (error) {
              if (!isNotFound(error)) throw error;
            }
          },
          'Failed to delete the SCORM package',
        );
        if (ok) patchStep(step.clientId, () => ({ removeScorm: true }));
      },

      startVideoUpload: (step, stepNumber, file, currentVideo) => {
        if (!step.serverId) return;
        patchStep(step.clientId, () => ({ removeVideo: false }));
        videoUploader.start(step.serverId, file, { stepNumber, lessonTitle }, currentVideo);
      },

      cancelVideoUpload: (step) => {
        if (step.serverId) videoUploader.cancel(step.serverId);
      },

      retryVideoUpload: (step) => {
        if (step.serverId) videoUploader.retry(step.serverId);
      },

      startScormUpload: (step, stepNumber, file, currentScorm) => {
        if (!step.serverId) return;
        patchStep(step.clientId, () => ({ removeScorm: false }));
        scormUploader.start(
          step.serverId,
          file,
          { stepNumber, lessonTitle },
          { graded: step.scormGraded, passingScore: step.scormPassingScore },
          currentScorm,
        );
      },

      cancelScormUpload: (step) => {
        if (step.serverId) scormUploader.cancel(step.serverId);
      },

      retryScormUpload: (step) => {
        if (step.serverId) scormUploader.retry(step.serverId);
      },

      isAdding: !!saving[ADD_KEY],
      isStepSaving: (clientId) => !!saving[clientId],
    }),
    [
      lessonId,
      lessonTitle,
      run,
      patchStep,
      setSteps,
      saving,
      videoUploader,
      scormUploader,
      createLessonStep,
      updateContentStep,
      deleteContentStep,
      reorderLessonSteps,
      updateContentStepScorm,
      deleteContentStepVideo,
      deleteContentStepScorm,
    ],
  );

  const isBusy = Object.keys(saving).length > 0;

  return { actions, isBusy };
};
